import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import { Box, Button } from "@mui/material";
import UploadMultipleIcon from "@iconify/icons-mdi/upload-multiple";
import { InlineIcon } from "@iconify/react";
import {
  myDocumentsUserListMobileTableDef,
  myDocumentsUserListTableDef,
} from "my-documents/configs";
import { useMyDocumentsUsers } from "my-documents/hooks";
import { MyDocumentsMassUploadFiles } from "my-documents/components/mass-upload-files";
import { MyDocumentsMassUploadHistory } from "my-documents/components";
import { useMassUploadsList } from "my-documents/hooks";
import {
  OcCard,
  OcDataTable,
  OcPageNameTitle,
  OcPageTransition,
  OcRoleFilter,
} from "oc/components";
import { ProductsT, RoleCategoriesT } from "oc/configs/constants";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcTableState,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcRoleFilters,
} from "oc/hooks";

export interface MyDocumentsUploadDocumentsPageProps {
  product?: ProductsT;
  role?: RoleCategoriesT;
}
export const MyDocumentsUploadDocumentsPage = ({
  product = "MyDocuments",
  role = "uploader",
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();

  const [filterable, setFilterable] = useState(false);
  const [showMassUpload, setShowMassUpload] = useState(false);

  let { contractCode: contract } = useOcCurrentUserState();

  // Mass uploads list refresh
  const { loadQuery: refreshMassUploads } = useMassUploadsList();

  let tableId = "myDocumentsUploaderTableData";
  const tableInitialData = {
    ...initialTableData,
    sortField: "name",
    pageSize: 5,
  };

  const [tableData, setTableData] = useOcTableState(tableId, tableInitialData);
  const [prevTableData, setPrevTableData] = useOcTableState(
    `${tableId}Prev`,
    tableInitialData,
  );

  const handleCompleted = () => {
    handleChangeFilterable(false);
  };

  const {
    roleFilters,
    prevRoleFilters,
    resetRoleFilters,
    setPrevRoleFilters,
    handleAddFilter,
    handleRemoveFilter,
  } = useOcRoleFilters({
    contract: contract,
    product: product,
    role: role,
  });

  const { loadQuery, data, loading } = useMyDocumentsUsers({
    roleFilters,
    filterable,
    tableData,
    role: role,
    onCompleted: handleCompleted,
  });

  const handleClearFilters = () => {
    resetRoleFilters();
  };

  const handleChangeFilterable = (state: boolean) => {
    setFilterable(state);
  };

  const buttonDisable = _.isEqual(roleFilters, prevRoleFilters);

  const handleLoadQuery = () => {
    loadQuery();
    setPrevRoleFilters(roleFilters);
  };

  const list = {
    rows: data?.rows,
    total: data?.total,
  };

  const [selectedRowId, setSelectedRowId] = useState<
    string | number | undefined
  >(undefined);

  const originalonSelectRow = (o: any) => {
    if (isMobile) {
      history.push(`/my-documents/user-files/${o?.row?.employee}/${o?.row?.contract}`);
    } else {
      history.push(`/my-documents/user-files/${o.employee}/${o.contract}`);
    }
  };

  const onSelectRow = (o: any) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? undefined : o?._id);
    } else {
      originalonSelectRow(o);
    }
  };

  const handleTableDataChange = (state: any) => {
    const keys = Object.keys(state).toString();
    setTableData(state);
    if (
      keys === "searchField" ||
      (keys === "searchText" && state?.searchText?.length < 3)
    ) {
      return;
    }
    loadQuery();
  };

  const tableDef = isMobile
    ? myDocumentsUserListMobileTableDef
    : myDocumentsUserListTableDef;

  const filteredTableDef = {
    id: myDocumentsUserListTableDef?.id,
    columns: myDocumentsUserListTableDef?.columns?.filter(
      (column) =>
        !myDocumentsUserListMobileTableDef?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const isTabledataChanged = !_.isEqual(tableData, prevTableData);
  useEffect(() => {
    if (isTabledataChanged) {
      handleLoadQuery();
      setPrevTableData(tableData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabledataChanged]);

  useEffect(() => {
    loadQuery();
    setPrevRoleFilters(roleFilters);
  }, []); // eslint-disable-line

  const handlePrimaryButton = () => {
    handleLoadQuery();
    handleChangeFilterable(true);
  };

  return (
    <OcPageTransition>
      <OcPageNameTitle
        title={t("MY_DOCUMENTS_UPLOAD_PAGE_TITLE")}
        goBack={false}
      />
      <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
        {showMassUpload ? (
          <Box>
            <MyDocumentsMassUploadFiles
              setShowNewCard={setShowMassUpload}
              onUploadComplete={refreshMassUploads}
            />
            <Box height="16px" />
            <MyDocumentsMassUploadHistory />
          </Box>
        ) : (
          <Box>
            <OcCard
              label={t("MY_DOCUMENTS_MANAGING_DOCUMENTS")}
              loading={loading}
            >
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  id="massUploadButton"
                  variant="contained"
                  color="primary"
                  startIcon={<InlineIcon icon={UploadMultipleIcon} />}
                  onClick={() => setShowMassUpload(true)}
                  disableElevation
                >
                  {t("MY_DOCUMENTS_MASS_UPLOAD")}
                </Button>
              </Box>
              <OcRoleFilter
                subtypeProps={{
                  roleFilters,
                  handleAddItem: handleAddFilter,
                  handleRemoveItem: handleRemoveFilter,
                  product: product as ProductsT,
                  role: role as RoleCategoriesT,
                  queryType: "notAdmin",
                  isCheckbox: true,
                }}
                buttonBarProps={{
                  handlePrimaryButton,
                  handleRightButton: handleClearFilters,
                  primaryButtonDisabled: buttonDisable,
                }}
                summaryItems={[
                  {
                    label: "OC_TOTAL_COUNT_OF_EMPLOYEES",
                    value: data?.allUsersCount || 0,
                  },
                ]}
              />
              <Box paddingTop="32px">
                <OcDataTable
                  data={list}
                  loading={loading}
                  tableDef={tableDef}
                  tableData={tableData}
                  setTableData={handleTableDataChange}
                  onSelectRow={onSelectRow}
                  tableId={tableId}
                  pagination={true}
                  filteredTableDef={filteredTableDef}
                  selectedRowId={selectedRowId}
                  handleMoreButton={originalonSelectRow}
                  isNotExaminer={false}
                />
              </Box>
            </OcCard>
          </Box>
        )}
      </Box>
    </OcPageTransition>
  );
};

export default MyDocumentsUploadDocumentsPage;
